import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import type { LiveSeoContent as LiveSeoPageData } from "~/data/liveSeoContent.functions";
import styles from "../live-seo-page.module.css";

type HappyHalloweenLocalization = {
	h1: string;
	date: string;
	paragraphs: string[];
	relatedTitle: string;
	relatedWhyDate: string;
	relatedWhyTitle: string;
	relatedMissionDate: string;
	relatedMissionTitle: string;
};

const happyHalloweenLocalization = {
	[Locale.EN]: {
		h1: "Happy Halloween!",
		date: "October 31, 2016",
		paragraphs: [
			"It seems that there is a month or day to celebrate everything. There’s National Sibling Day, National Brush Day, and my personal favorite, National Chocolate Chip Cookie Day! Today, in addition to being Halloween, is National Caramel Apple Day!",
			"We’re also wrapping up National Orthodontic Health Month. Orthodontic health is about making sure that you are taking care of those chompers while undergoing treatment with braces or Invisalign and beyond! Here at Miami Orthodontic Specialists, we are also celebrating that our new office is open and that we can begin to share the gift of beautiful smiles in our new space.",
			"P.S. If you have braces on, please don’t celebrate National Caramel Apple Day! 😉",
		],
		relatedTitle: "Also Worth Checking Out",
		relatedWhyDate: "Nov 23, 2016",
		relatedWhyTitle: "Why did you become an orthodontist?",
		relatedMissionDate: "Jan 22, 2016",
		relatedMissionTitle: "Our Mission",
	},
	[Locale.ES]: {
		h1: "¡Feliz Halloween!",
		date: "31 de octubre de 2016",
		paragraphs: [
			"Parece que hay un mes o un día para celebrar todo. Está el Día Nacional de los Hermanos, el Día Nacional del Cepillado y mi favorito personal, el Día Nacional de la Galleta con Chispas de Chocolate. Hoy, además de ser Halloween, es el Día Nacional de la Manzana Acaramelada.",
			"También estamos terminando el Mes Nacional de la Salud Ortodóncica. La salud ortodóncica se trata de asegurarte de cuidar esos dientes mientras estás en tratamiento con brackets o Invisalign, y también después. Aquí en Miami Orthodontic Specialists, también celebramos que nuestra nueva oficina está abierta y que podemos comenzar a compartir el regalo de sonrisas hermosas en nuestro nuevo espacio.",
			"P.D. Si tienes brackets, por favor no celebres el Día Nacional de la Manzana Acaramelada. 😉",
		],
		relatedTitle: "También vale la pena leer",
		relatedWhyDate: "23 de noviembre de 2016",
		relatedWhyTitle: "¿Por qué te convertiste en ortodoncista?",
		relatedMissionDate: "22 de enero de 2016",
		relatedMissionTitle: "Nuestra misión",
	},
} satisfies Record<Locale, HappyHalloweenLocalization>;

export function HappyHalloweenPage({ page }: { page: LiveSeoPageData }) {
	const getLocalization = useLocalization(happyHalloweenLocalization);
	const paragraphs = getLocalization("paragraphs");

	return (
		<Flex direction="column">
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/miami-ortho-4.jpg"
						alt="Children engaging with interactive tablets in a welcoming orthodontic office, showcasing Miami Orthodontic Specialists' family-friendly environment and patient-centered care."
						width={page.image?.width}
						height={page.image?.height}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 760px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{getLocalization("h1")}
					</Heading>
					<Text as="p" className={styles.postDate}>
						{getLocalization("date")}
					</Text>
					<Box className={styles.entryContent}>
						{paragraphs.map((paragraph) => (
							<Text as="p" size="4" key={paragraph}>
								{paragraph}
							</Text>
						))}
					</Box>
					<section className={styles.relatedPosts}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("relatedTitle")}
						</Heading>
						<Grid columns={{ initial: "1", sm: "2" }} gap="5">
							<Link
								to="/why-i-became-an-orthodontist"
								className={styles.relatedCard}
							>
								<OptimizedImage
									src="/miami-ortho-13.jpg"
									alt="Smiling children with braces and an orthodontist in Miami, surrounded by greenery, promoting Miami Orthodontic Specialists' family-friendly care."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedWhyDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedWhyTitle")}
									</Heading>
								</Box>
							</Link>
							<Link
								to="/welcome-miami-orthodontics"
								className={styles.relatedCard}
							>
								<OptimizedImage
									src="/same-day.jpg"
									alt="Young girl in orthodontic chair, smiling and giving a thumbs up, showcasing braces in Miami orthodontic office with dental equipment and x-ray monitor in background."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedMissionDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedMissionTitle")}
									</Heading>
								</Box>
							</Link>
						</Grid>
					</section>
				</Box>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
